import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Phone } from 'lucide-react';
import useMeta from '../hooks/useMeta';

const ServicePage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  const title = service ? `${service.title.da} i Tønder | SL Flise- og murerforretning` : 'SL Flise- og murerforretning';
  const description = service ? `Professionel ${service.title.da.toLowerCase()} i Tønder og omegn. SL Flise- og murerforretning har 25 års erfaring og sikrer høj kvalitet og præcision. Få et uforpligtende tilbud!` : '';
  const ogImage = service?.image?.[0] || '';

  useMeta(title, description, ogImage);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`);
        const data = await response.json();
        setService(data);
      } catch (error) {
        console.error('Error fetching service:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchService();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin h-12 w-12 border-t-2 border-b-2 border-orange-600"></div>
    </div>
  );

  if (!service) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4">
      <h1 className="text-4xl font-bold text-slate-900 mb-4">Ydelsen blev ikke fundet</h1>
      <Link to="/" className="text-orange-600 font-bold flex items-center">
        <ArrowLeft className="mr-2 h-5 w-5" /> Gå tilbage til forsiden
      </Link>
    </div>
  );

  const displayContent = service.content?.da || service.description?.da;

  return (
    <div className="pt-32 md:pt-40 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/#ydelser" className="inline-flex items-center text-slate-500 hover:text-orange-600 font-medium mb-12 transition-colors group">
          <ArrowLeft className="mr-2 h-5 w-5 group-hover:-translate-x-1 transition-transform" />
          Tilbage til alle ydelser
        </Link>

        <div className="flex flex-col lg:flex-row gap-16">
          <div className="lg:w-1/2">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">{service.title.da}</h1>
            <div className="h-1.5 w-20 bg-orange-600 mb-8"></div>
            
            <div 
              className="prose prose-lg prose-slate max-w-none mb-12"
              dangerouslySetInnerHTML={{ __html: displayContent }}
            />

            <div className="bg-slate-50 p-8 border border-slate-100 mb-12">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Hvorfor vælge os til {service.title.da.toLowerCase()}?</h3>
              <ul className="space-y-4">
                {[
                  'Høj faglig stolthed og kvalitet',
                  'Erfaring siden 2001 (ca. 25 år i faget)',
                  'Lokal murer i Tønder og omegn',
                  'Konkurrencedygtige priser',
                  'Overholdelse af alle aftaler og tidsplaner'
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <Check className="h-6 w-6 text-slate-900 shrink-0" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/#kontakt"
                className="inline-flex items-center justify-center bg-orange-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-orange-700 transition-all"
              >
                Få et tilbud på opgaven
              </a>
              <a
                href="tel:+4561678352"
                className="inline-flex items-center justify-center bg-white border-2 border-slate-200 text-slate-900 px-8 py-4 rounded-full font-bold text-lg hover:border-orange-600 hover:text-orange-600 transition-all"
              >
                <Phone className="mr-2 h-5 w-5" />
                Ring op nu
              </a>
            </div>
          </div>

          <div className="lg:w-1/2">
            <div className="sticky top-32">
              <div className="overflow-hidden shadow-2xl">
                <img
                  src={service.image[0]}
                  alt={service.title.da}
                  className="w-full h-auto object-cover"
                />
              </div>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="bg-orange-600 p-6 text-white">
                  <p className="text-white text-xs font-bold uppercase tracking-wider mb-1">Erfaring</p>
                  <p className="text-2xl font-bold">Siden 2001</p>
                </div>
                <div className="bg-slate-900 p-6 text-white">
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1">Lokation</p>
                  <p className="text-2xl font-bold">Tønder</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicePage;
